<script setup lang="ts">
/**
 * KpiCard — small "uppercase label + big number" tile used in queue
 * counters, pipeline summaries, and dashboard KPIs.
 *
 * One card per stage / status. The `highlight` flag pulls the card into
 * a warning-tinted state — used for "needs attention" stages (e.g.
 * AML queue depth, in-compliance queue depth) where the operator
 * should glance at it first.
 *
 * For richer KPIs with icons + change indicators, see the dashboard's
 * own `DashboardKpiCard.vue` — that shape is intentionally separate
 * (richer surface, more decoration, dashboard-only).
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Skeleton from '@uiKit/elements/Skeleton.vue'

interface Props {
  /** Uppercase label above the value. */
  label: string
  /** Main value — string so callers can pre-format (`'1,234'`, `'AED 5M'`). */
  value: string | number
  /** Optional muted-caption subtitle below the value (e.g. "vs last week"). */
  subtitle?: string
  /** Render a skeleton in place of the value while data is pending. */
  loading?: boolean
  /** Warning-tinted card chrome — for "needs attention" stages. Deprecated, use variant="warning". */
  highlight?: boolean
  /** Semantic variant for card chrome. */
  variant?: 'default' | 'warning' | 'success'
  /** Render as an interactive control (pointer cursor, hover lift, keyboard-activatable). */
  clickable?: boolean
  /** Selected/active chrome — for the active card in a filter-card row. */
  selected?: boolean
  /** Skeleton dimensions (when `loading`). Tune per layout if needed. */
  loadingWidth?: string
  loadingHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  highlight: false,
  variant: 'default',
  clickable: false,
  selected: false,
  loadingWidth: '64px',
  loadingHeight: '36px',
})

const emit = defineEmits<{ click: [] }>()

function handleActivate() {
  if (props.clickable) emit('click')
}
</script>

<template>
  <Box
    class="kpi-card"
    :class="[
      `kpi-card--${props.variant}`,
      {
        'kpi-card--warning': props.highlight && props.variant === 'default',
        'kpi-card--clickable': props.clickable,
        'kpi-card--selected': props.selected,
      },
    ]"
    :role="props.clickable ? 'button' : undefined"
    :tabindex="props.clickable ? 0 : undefined"
    :aria-pressed="props.clickable ? props.selected : undefined"
    @click="handleActivate"
    @keydown.enter.prevent="handleActivate"
    @keydown.space.prevent="handleActivate"
  >
    <Text
      type="caption-sm"
      transform="uppercase"
      weight="semibold"
      color="muted"
      class="kpi-card__label"
    >
      {{ label }}
    </Text>

    <Skeleton v-if="loading" :height="loadingHeight" :width="loadingWidth" />
    <Text v-else type="display" weight="bold" class="kpi-card__value">
      {{ value }}
    </Text>

    <Text v-if="subtitle && !loading" type="caption" color="muted">
      {{ subtitle }}
    </Text>
  </Box>
</template>

<style scoped>
.kpi-card {
  flex: 1;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  min-width: 0;
}

.kpi-card--warning {
  border-color: var(--p-warning-400);
  background: var(--p-surface-0);
}

.kpi-card--clickable {
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background 0.15s ease,
    box-shadow 0.15s ease;
}

.kpi-card--clickable:hover {
  border-color: var(--p-primary-400);
}

.kpi-card--clickable:focus-visible {
  outline: 2px solid var(--p-primary-400);
  outline-offset: 2px;
}

.kpi-card--selected {
  border-color: var(--p-primary-600);
  background: var(--p-primary-50);
  box-shadow: inset 0 0 0 1px var(--p-primary-600);
}

.kpi-card--success {
  border-color: var(--p-success-300);
  background: var(--p-success-50, rgba(34, 197, 94, 0.04));
}

.kpi-card__label {
  letter-spacing: 0.06em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
